import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Person as PersonIcon } from '@mui/icons-material';
import apiAxiosInstance from '../../../utils/axiosInstance';
import { useTeacher } from '../../../context/TeacherContext';

interface AddStudentDialogProps {
  open: boolean;
  onClose: () => void;
  onStudentAdded: (student: any) => void;
}

const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const AddStudentDialog: React.FC<AddStudentDialogProps> = ({
  open,
  onClose,
  onStudentAdded,
}) => {
  const { teacher } = useTeacher();
  const [activeStep, setActiveStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingPeriods, setExistingPeriods] = useState<string[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [testConnection, setTestConnection] = useState(false);

  const steps = ['Name', 'Classroom Info'];

  // Test API connection
  const testApiConnection = async () => {
    setTestConnection(true);
    console.log('[FRONTEND] Testing API connection...');
    try {
      const response = await apiAxiosInstance.get('/health');
      console.log('[FRONTEND] API health check:', response.data);
    } catch (err: any) {
      console.error('[FRONTEND] API health check failed:', err);
    } finally {
      setTestConnection(false);
    }
  };

  // Auto-set grade level to teacher's main grade level when dialog opens
  useEffect(() => {
    if (open) {
      console.log('[FRONTEND] Dialog opened - Teacher data:', {
        _id: teacher?._id,
        firstName: teacher?.firstName,
        lastName: teacher?.lastName,
        gradeLevel: teacher?.gradeLevel,
        email: teacher?.email
      });

      if (teacher?.gradeLevel && !gradeLevel) {
        console.log('[FRONTEND] Auto-setting grade level to:', teacher.gradeLevel);
        setGradeLevel(teacher.gradeLevel);
      }

      // Load existing periods for this teacher
      loadExistingPeriods();
    }
  }, [open, teacher?.gradeLevel, gradeLevel]);

  // Load existing periods for the teacher
  const loadExistingPeriods = async () => {
    if (!teacher?._id) return;

    setPeriodsLoading(true);
    try {
      console.log('[FRONTEND] Loading existing periods for teacher:', teacher._id);
      const response = await apiAxiosInstance.get('/api/students', {
        params: { teacherId: teacher._id }
      });

      // Extract unique, non-empty periodIds
      const periods = response.data
        .map((student: any) => student.periodId)
        .filter((period: string | null | undefined) => period && period.trim() !== '')
        .filter((period: string, index: number, self: string[]) => self.indexOf(period) === index); // Remove duplicates

      console.log('[FRONTEND] Found existing periods:', periods);
      setExistingPeriods(periods);
    } catch (err) {
      console.error('[FRONTEND] Error loading periods:', err);
      // Continue without showing error - periods are optional
    } finally {
      setPeriodsLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setGradeLevel('');
    setPeriodId('');
    setError(null);
    onClose();
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!firstName.trim() || !lastName.trim()) {
        setError('First name and last name are required');
        return;
      }
      if (!gradeLevel) {
        setError('Grade level is required');
        return;
      }
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!gradeLevel) {
      setError('Grade level is required');
      return;
    }

    if (!teacher?._id) {
      setError('You must be logged in to add a student');
      return;
    }

    setLoading(true);
    setError(null);

    const studentData = {
      teacherId: teacher._id,
      firstName: firstName.trim(),
      middleName: middleName.trim() || undefined,
      lastName: lastName.trim(),
      gradeLevel: gradeLevel,
      periodId: periodId.trim() || undefined,
      hasBehaviorCases: false,
    };

    console.log('[FRONTEND] Submitting student data:', JSON.stringify(studentData, null, 2));
    console.log('[FRONTEND] Teacher info:', {
      _id: teacher._id,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      gradeLevel: teacher.gradeLevel
    });

    try {
      const response = await apiAxiosInstance.post('/api/students', studentData);
      console.log('[FRONTEND] Success response:', response.data);
      onStudentAdded(response.data.student ?? response.data);
      handleClose();
    } catch (err: any) {
      console.error('[FRONTEND] Full error object:', err);
      console.error('[FRONTEND] Error name:', err.name);
      console.error('[FRONTEND] Error message:', err.message);
      console.error('[FRONTEND] Error code:', err.code);
      console.error('[FRONTEND] Error config:', err.config);
      console.error('[FRONTEND] Error response:', err.response);
      console.error('[FRONTEND] Error request:', err.request);

      if (err.response) {
        console.error('[FRONTEND] Error response data:', err.response.data);
        console.error('[FRONTEND] Error response status:', err.response.status);
        console.error('[FRONTEND] Error response headers:', err.response.headers);
      } else if (err.request) {
        console.error('[FRONTEND] Error - no response received:', err.request);
      } else {
        console.error('[FRONTEND] Error - request setup failed:', err.message);
      }

      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to add student';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name *"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Middle Name"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name *"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Grade Level *</InputLabel>
                <Select
                  value={gradeLevel}
                  label="Grade Level"
                  onChange={(e) => setGradeLevel(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Grade</em>
                  </MenuItem>
                  {GRADE_LEVELS.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={existingPeriods}
                value={periodId}
                onChange={(event, newValue) => {
                  setPeriodId(newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  setPeriodId(newInputValue);
                }}
                loading={periodsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Classroom / Period"
                    placeholder="e.g., Room 101 - 1st Period, Math Lab - Block A"
                    helperText={
                      existingPeriods.length > 0
                        ? `Existing: ${existingPeriods.slice(0, 3).join(', ')}${existingPeriods.length > 3 ? '...' : ''}`
                        : 'Enter both classroom name and period together'
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {periodsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                getOptionLabel={(option) => option}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );

                  // Suggest the input value as a new option if it's not empty
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }

                  return filtered;
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Student Preview:
                </Typography>
                <Typography variant="body1">
                  {firstName} {middleName && `${middleName} `}{lastName}
                </Typography>
                {gradeLevel && (
                  <Typography variant="body2" color="text.secondary">
                    Grade {gradeLevel}
                  </Typography>
                )}
                {periodId && (
                  <Typography variant="body2" color="text.secondary">
                    {periodId}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Add New Student
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={testApiConnection}
          disabled={testConnection}
          sx={{ ml: 1 }}
        >
          Test API
        </Button>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!firstName.trim() || !lastName.trim() || !gradeLevel}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !firstName.trim() || !lastName.trim() || !gradeLevel}
            startIcon={<AddIcon />}
          >
            {loading ? 'Adding...' : 'Add Student'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentDialog;
